import type {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from 'react-query';
import { useQuery } from 'react-query';

type UseExportOrganizationJobsToFileInput = {
  fileType: string;
  fromDate: string;
  toDate: string;
  assignedStatus?: 'open' | 'assigned';
  jobType?: 'interest' | 'urgent' | 'personal' | undefined;
  selectedFreelancers?: Array<number>;
  selectedLocations?: Array<number>;
  selectedOrganizations?: Array<number>;
  page: string | null;
  pageSize: string | null;
  isPagination?: boolean;
};

type UseExportOrganizationJobsToFileHandler = {
  isLoading?: boolean;
  error: string | null;
  data?: Blob;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => Promise<QueryObserverResult<Blob, string>>;
};

const useExportOrganisationJobsToFile = (
  params: UseExportOrganizationJobsToFileInput,
): UseExportOrganizationJobsToFileHandler => {
  const queryFn = async () => {
    const query = new URLSearchParams();
    query.append('file_type', params.fileType);
    query.append('from_date', params.fromDate);
    query.append('to_date', params.toDate);

    if (params.assignedStatus) {
      query.append('assigned_status', params.assignedStatus);
    }

    if (params.jobType) {
      query.append('job_type', params.jobType);
    }

    if (
      typeof params?.selectedFreelancers === 'object' &&
      params?.selectedFreelancers?.length > 0
    ) {
      params?.selectedFreelancers.forEach((item) =>
        query.append('assigned_to', item.toString()),
      );
    }

    if (
      typeof params?.selectedOrganizations === 'object' &&
      params?.selectedOrganizations?.length > 0
    ) {
      params?.selectedOrganizations.forEach((item) =>
        query.append('organisation', item.toString()),
      );
    }

    if (
      typeof params?.selectedLocations === 'object' &&
      params?.selectedLocations?.length > 0
    ) {
      params?.selectedLocations.forEach((item) =>
        query.append('location', item.toString()),
      );
    }

    if (params.isPagination) {
      query.append('pagination', 'True');
      query.append('page', params.page?.toString() as string);
      query.append('page_size', params.pageSize?.toString() as string);
    }

    const response = await fetch(
      `/api/organisation/jobs/export?${new URLSearchParams(query)}`,
    );
    if (response.ok) return response.blob();
    throw new Error('Something went wrong');
  };

  const { isLoading, error, refetch, data } = useQuery<Blob, string>(
    'export-organisation-jobs-to-csv',
    queryFn,
    {
      enabled: false,
    },
  );

  return { data, error, isLoading, refetch };
};

export default useExportOrganisationJobsToFile;
